import React, { useState } from 'react';
import './styles/OrderItemsCard.css';

interface OrderItem {
  _id?: string;
  product?: {
    title?: string;
    imageUrl?: string;
  };
  variantTitle?: string;
  variantImage?: string;
  quantity?: number;
  price?: number;
}

interface OrderItemsCardProps {
  items: OrderItem[];
  orderId?: string;
  totalAmount?: number;
  subtotal?: number;
  shippingCost?: number;
  totalGST?: number;
  couponDiscount?: number;
  appliedCredits?: number;
}

const OrderItemsCard: React.FC<OrderItemsCardProps> = ({ 
  items, 
  totalAmount, 
  subtotal,
  shippingCost,
  totalGST,
  couponDiscount = 0,
  appliedCredits = 0
}) => {
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [showAllItems] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleImageError = (imageUrl: string) => {
    setImageErrors(prev => new Set(prev).add(imageUrl));
  };

  const getImageUrl = (item: OrderItem) => {
    const variantImage = item.variantImage;
    const productImage = item.product?.imageUrl;
    
    if (variantImage && !imageErrors.has(variantImage)) {
      return variantImage;
    }
    
    if (productImage && !imageErrors.has(productImage)) {
      return productImage;
    }
    
    return '/images/dummy.png';
  };

  const getItemName = (item: OrderItem) => {
    return item.variantTitle || item.product?.title || 'Product';
  };

  const displayItems = showAllItems ? items : items.slice(0, 2);
  const remainingCount = items.length - 2;

  return (
    <div className="order-items-card">
      <div className="order-items-header">
        <div className="order-items-left">
          <h2 className="order-items-title">Items ordered</h2>
          <p className="order-items-summary">
            {items.length} {items.length === 1 ? 'item' : 'items'} • Total: ₹{totalAmount ? Math.round(totalAmount).toLocaleString() : '0'}
          </p>
          <button className="view-details-btn" onClick={() => setShowModal(true)}>
            View details
            <svg className="dropdown-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <div className="order-items-right">
          <div className="items-grid">
            {displayItems.map((item, index) => (
              <div key={index} className="item-card">
                <img
                  src={getImageUrl(item)}
                  alt={getItemName(item)}
                  className="item-image"
                  onError={() => handleImageError(getImageUrl(item))}
                />
                <p className="item-name">
                  {getItemName(item)} ({item.quantity || 1})
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>


      {/* Expanded items view */}
      {showAllItems && remainingCount > 0 && (
        <div className="expanded-items">
          {items.slice(3).map((item, index) => (
            <div key={index + 3} className="expanded-item">
              <img
                src={getImageUrl(item)}
                alt={getItemName(item)}
                className="expanded-item-image"
                onError={() => handleImageError(getImageUrl(item))}
              />
              <div className="expanded-item-details">
                <p className="expanded-item-name">{getItemName(item)}</p>
                <p className="expanded-item-quantity">Quantity: {item.quantity || 1}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Details Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>
              ✕
            </button>
            <h2 className="modal-title">Order Details</h2>
            <div className="modal-items-list">
              {items.map((item, index) => (
                <div key={index} className="modal-item-row">
                  <img
                    src={getImageUrl(item)}
                    alt={getItemName(item)}
                    className="modal-item-row-image"
                    onError={() => handleImageError(getImageUrl(item))}
                  />
                  <div className="modal-item-row-details">
                    <p className="modal-item-row-name">{getItemName(item)}</p>
                    <p className="modal-item-row-quantity">Quantity: {item.quantity || 1}</p>
                  </div>
                  <p className="modal-item-row-price">₹{item.price ? Math.round(item.price).toLocaleString() : '0'}</p>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="order-summary">
              <h3 className="summary-title">Order Summary</h3>
              
              <div className="summary-row">
                <span>Subtotal</span>
                <span>₹{subtotal ? Math.round(subtotal).toLocaleString() : '0'}</span>
              </div>

              {totalGST && totalGST > 0 && (
                <div className="summary-row">
                  <span>GST</span>
                  <span>₹{Math.round(totalGST).toLocaleString()}</span>
                </div>
              )}

              <div className="summary-row">
                <span>Delivery Charges</span>
                <span className={shippingCost && shippingCost > 0 ? 'delivery-charge' : 'free-delivery'}>
                  {shippingCost && shippingCost > 0 ? `₹${Math.round(shippingCost).toLocaleString()}` : 'Free'}
                </span>
              </div>

              {(couponDiscount || appliedCredits) && (
                <div className="summary-section">
                  {couponDiscount > 0 && (
                    <div className="summary-row">
                      <span>Discount Coupons</span>
                      <span className="coupon-value">-₹{Math.round(couponDiscount).toLocaleString()}</span>
                    </div>
                  )}
                  {appliedCredits > 0 && (
                    <div className="summary-row">
                      <span>BuildCoins Used</span>
                      <span className="coupon-value">-₹{Math.round(appliedCredits).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}

              {!couponDiscount && !appliedCredits && (
                <div className="summary-section">
                  <div className="summary-row">
                    <span>Discount Coupons</span>
                    <span className="coupon-value">-₹0</span>
                  </div>
                  <p className="summary-hint">Apply coupon code to get discount</p>
                </div>
              )}

              <div className="summary-divider"></div>

              <div className="summary-row total-row">
                <span>Total Amount</span>
                <span className="total-amount">₹{totalAmount ? Math.round(totalAmount).toLocaleString() : '0'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderItemsCard;
